import React, { useEffect, useRef, useState } from 'react';
import { uploads as uploadsApi } from '@/utils/api';
import { formatSeconds } from '@/utils/videoThumbnail';
import { MessageSquarePlus, Scissors, Trash2, Save, X, Play } from 'lucide-react';
import { toast } from 'sonner';

/**
 * AnnotatedVideoPlayer — HTML5 video with timeline markers, annotations sidebar,
 * and simple trim (in/out) controls. isOperator enables all admin actions.
 */
export default function AnnotatedVideoPlayer({ upload, onUpdated, isOperator = true, authorView = false }) {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(upload.duration_s || 0);
  const [currentTime, setCurrentTime] = useState(0);
  const [annotations, setAnnotations] = useState(upload.annotations || []);
  const [trim, setTrim] = useState(upload.trim || { start_s: 0, end_s: null });
  const [adding, setAdding] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [trimEditing, setTrimEditing] = useState(false);

  const canEdit = isOperator || authorView;

  const handleMeta = () => {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    if (trim.start_s && v.currentTime < trim.start_s) v.currentTime = trim.start_s;
  };

  const handleTime = () => {
    const v = videoRef.current;
    if (!v) return;
    setCurrentTime(v.currentTime);
    if (trim.end_s && v.currentTime >= trim.end_s) {
      v.pause();
    }
  };

  const jumpTo = (t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    v.play().catch(() => {});
  };

  const addAnnotation = async () => {
    const v = videoRef.current;
    if (!v || !noteText.trim()) return;
    try {
      const r = await uploadsApi.addAnnotation(upload.id, v.currentTime, noteText.trim());
      setAnnotations(prev => [...prev, r.data]);
      setNoteText('');
      setAdding(false);
      toast.success('Note added');
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const delAnnotation = async (annId) => {
    try {
      await uploadsApi.deleteAnnotation(upload.id, annId);
      setAnnotations(prev => prev.filter(a => a.id !== annId));
      onUpdated?.();
    } catch (e) { toast.error('Failed'); }
  };

  const saveTrim = async () => {
    try {
      await uploadsApi.setTrim(upload.id, trim.start_s || 0, trim.end_s || null);
      toast.success('Trim saved');
      setTrimEditing(false);
      onUpdated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const clearTrim = async () => {
    setTrim({ start_s: 0, end_s: null });
    try {
      await uploadsApi.setTrim(upload.id, 0, null);
      onUpdated?.();
    } catch (e) {}
  };

  const setTrimFromNow = (which) => {
    const v = videoRef.current;
    if (!v) return;
    setTrim(prev => ({ ...prev, [which]: Number(v.currentTime.toFixed(2)) }));
  };

  const sortedAnns = [...annotations].sort((a, b) => a.timestamp_s - b.timestamp_s);

  return (
    <div className="flex flex-col lg:flex-row gap-3" data-testid="annotated-video-player">
      {/* Player */}
      <div className="flex-1 min-w-0">
        <div className="relative bg-black rounded-card overflow-hidden">
          <video
            ref={videoRef}
            src={upload.public_url}
            controls
            className="w-full"
            style={{ maxHeight: '60vh' }}
            onLoadedMetadata={handleMeta}
            onTimeUpdate={handleTime}
            poster={upload.thumbnail_url || undefined}
            data-testid="video-player"
          />
        </div>

        {/* Timeline + markers */}
        <div className="mt-2">
          <div className="relative h-8 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            {/* Trim range overlay */}
            {duration > 0 && (trim.start_s > 0 || trim.end_s) && (
              <div
                className="absolute top-0 bottom-0 rounded-btn"
                style={{
                  left: `${(trim.start_s / duration) * 100}%`,
                  width: `${(((trim.end_s || duration) - trim.start_s) / duration) * 100}%`,
                  background: '#D9A44125',
                  border: '1px solid #D9A441',
                }}
              />
            )}
            {/* Current time marker */}
            {duration > 0 && (
              <div
                className="absolute top-0 bottom-0 w-0.5"
                style={{ left: `${(currentTime / duration) * 100}%`, background: '#006080' }}
              />
            )}
            {/* Annotation dots */}
            {sortedAnns.map(a => (
              <button
                key={a.id}
                onClick={() => jumpTo(a.timestamp_s)}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full hover:scale-150 transition-transform"
                style={{ left: `${(a.timestamp_s / (duration || 1)) * 100}%`, background: '#5B2D8E', border: '2px solid white' }}
                title={`${formatSeconds(a.timestamp_s)} · ${a.note}`}
                data-testid={`ann-marker-${a.id}`}
              />
            ))}
          </div>
          <div className="flex items-center justify-between mt-1 text-xs font-mono" style={{ color: 'var(--muted)' }}>
            <span>{formatSeconds(currentTime)}</span>
            <span>{formatSeconds(duration)}</span>
          </div>
        </div>

        {/* Action buttons */}
        {canEdit && (
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={() => setAdding(true)}
              className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1.5"
              style={{ background: '#5B2D8E', color: 'white' }}
              data-testid="add-annotation-button"
            >
              <MessageSquarePlus size={12} /> Note at {formatSeconds(currentTime)}
            </button>
            {isOperator && (
              <>
                <button
                  onClick={() => setTrimEditing(!trimEditing)}
                  className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1.5"
                  style={{ background: trimEditing ? '#D9A441' : 'var(--bg)', color: trimEditing ? 'white' : 'var(--ink)', border: '1px solid var(--border)' }}
                  data-testid="trim-toggle"
                >
                  <Scissors size={12} /> Trim
                </button>
                {(trim.start_s > 0 || trim.end_s) && (
                  <button onClick={clearTrim} className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: 'var(--bg)', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                    Clear trim
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Trim editor */}
        {trimEditing && (
          <div className="mt-3 p-3 rounded-btn" style={{ background: '#D9A44110', border: '1px solid #D9A44140' }} data-testid="trim-editor">
            <p className="text-xs font-bold mb-2" style={{ color: '#D9A441' }}>Trim playback range</p>
            <div className="flex items-center gap-2 text-xs">
              <button onClick={() => setTrimFromNow('start_s')} className="px-2 py-1 rounded-btn font-semibold" style={{ background: 'white', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                Set IN → now ({formatSeconds(currentTime)})
              </button>
              <span className="font-mono">IN {formatSeconds(trim.start_s)}</span>
              <span>•</span>
              <button onClick={() => setTrimFromNow('end_s')} className="px-2 py-1 rounded-btn font-semibold" style={{ background: 'white', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                Set OUT → now ({formatSeconds(currentTime)})
              </button>
              <span className="font-mono">OUT {trim.end_s != null ? formatSeconds(trim.end_s) : '—'}</span>
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={saveTrim} className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" data-testid="trim-save">
                <Save size={11} /> Save trim
              </button>
              <button onClick={() => setTrimEditing(false)} className="text-xs px-3 py-1.5 rounded-btn" style={{ background: 'white', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {adding && (
          <div className="mt-3 p-3 rounded-btn" style={{ background: '#5B2D8E10', border: '1px solid #5B2D8E40' }} data-testid="add-annotation-form">
            <p className="text-xs font-bold mb-2" style={{ color: '#5B2D8E' }}>
              Add note at <span className="font-mono">{formatSeconds(currentTime)}</span>
            </p>
            <textarea
              className="input-agency text-sm resize-none"
              rows={2}
              placeholder="What's happening here?"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              autoFocus
              data-testid="annotation-input"
            />
            <div className="flex gap-2 mt-2">
              <button onClick={addAnnotation} disabled={!noteText.trim()} className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5" data-testid="annotation-save">
                <Save size={11} /> Save
              </button>
              <button onClick={() => { setAdding(false); setNoteText(''); }} className="text-xs px-3 py-1.5 rounded-btn" style={{ background: 'white', color: 'var(--muted)', border: '1px solid var(--border)' }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Annotations panel */}
      <div className="lg:w-72 flex-shrink-0">
        <div className="p-3 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)', maxHeight: '60vh', overflowY: 'auto' }}>
          <p className="text-xs font-bold uppercase mb-2" style={{ color: 'var(--muted)' }}>
            Annotations ({sortedAnns.length})
          </p>
          {sortedAnns.length === 0 ? (
            <p className="text-xs" style={{ color: 'var(--muted)' }}>No notes yet. Pause the video and click "Note at …" to add one.</p>
          ) : (
            <div className="space-y-1.5" data-testid="annotations-list">
              {sortedAnns.map(a => (
                <div key={a.id} className="p-2 rounded-btn" style={{ background: 'white', border: '1px solid var(--border)' }} data-testid={`annotation-${a.id}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <button onClick={() => jumpTo(a.timestamp_s)} className="text-xs font-bold font-mono flex items-center gap-1" style={{ color: '#5B2D8E' }}>
                      <Play size={8} /> {formatSeconds(a.timestamp_s)}
                    </button>
                    <button onClick={() => delAnnotation(a.id)} className="text-xs p-0.5" style={{ color: '#B34A3A' }} data-testid={`ann-del-${a.id}`}>
                      <Trash2 size={10} />
                    </button>
                  </div>
                  <p className="text-xs leading-snug" style={{ color: 'var(--ink)' }}>{a.note}</p>
                  <p className="text-[10px] mt-1" style={{ color: 'var(--muted)' }}>by {a.author_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
